import { React, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Navbar.css';
import Notification from './Notification';
import Chat from './Chat';

import { isAuthenticated, isStaff, isAdmin, logoutUser, updateUser } from '../../api/users_institutions';
import { getCurrentUser } from '../../api/users_institutions';
import { getPersistedEditorMode, setPersistedEditorMode } from '../../utils/editorMode';

const LANGUAGE_OPTIONS = ['en', 'fr', 'ar'];

function Navbar({
  updateEditorMode,
  editorMode = false,
  historyLogView = false,
  editorModeLite = false,
  onSave,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onIncreaseLessonSectionSize,
  onDecreaseLessonSectionSize,
  lessonSectionScale = 1,
  onHistory,
  viewOnlyMode = false
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAuthenticated = isAuthenticated();
  const userIsStaff = isStaff();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [localEditorMode, setLocalEditorMode] = useState(() => getPersistedEditorMode());
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && (window.matchMedia('(max-width: 760px)').matches || window.matchMedia('(max-height: 500px)').matches)
  );

  const [isTablet, setIsTablet] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches
  );

  const langDropdownRef = useRef(null);
  const currentLanguage = LANGUAGE_OPTIONS.includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    setIsLangMenuOpen(false);
  };


  const handleLoggingIn = () => {
    window.location.href = '/login';
  };

  const toggleEditorMode = () => {
    if (isMobile) return;
    const newState = !localEditorMode;
    setLocalEditorMode(newState);
    setPersistedEditorMode(newState);
    updateEditorMode?.(newState);
  };

  const toggleChatWidget = () => setIsChatOpen((open) => !open);

  const toggleNotifications = () => {
    const notifBox = document.getElementById('notifBox');
    if (notifBox) {
      if (notifBox.classList.contains('show')) {
        notifBox.classList.remove('show');
      } else {
        notifBox.classList.toggle('show');
        setBellBadgeVisible(false);
        // Persist server-side (User.new_announcement/new_notification, see api/models.py) so the
        // badge stays cleared for this account across devices/sessions, not just this tab's DOM
        // -- both are cleared together since the bell opens both tabs at once.
        const currentUser = getCurrentUser();
        if (currentUser?.id) {
          updateUser(currentUser.id, { new_announcement: false, new_notification: false }).catch(error => {
            console.error('Error clearing notification flag:', error);
          });
        }
      }
    }
  }

    const setBellBadgeVisible = (visible) => {
      document.querySelectorAll('.bell-notif-badge').forEach(el => {
          el.style.display = visible ? 'flex' : 'none';
      });
  };


  // Mobile Screen detected
  useEffect(() => {
    const widthMql = window.matchMedia('(max-width: 760px)');
    const heightMql = window.matchMedia('(max-height: 500px)');
    const handleChange = () => {
      const matches = widthMql.matches || heightMql.matches;
      setIsMobile(matches);
      if (matches) {
        updateEditorMode?.(false);
        setLocalEditorMode(false);
      }
    };
    // Run once immediately too -- the listeners below only fire on a *live* crossing, so a page
    // loaded directly into an already-short/narrow viewport (not resized down after mount) would
    // otherwise keep a persisted `true` editorMode switched on indefinitely.
    handleChange();
    widthMql.addEventListener('change', handleChange);
    heightMql.addEventListener('change', handleChange);
    return () => {
      widthMql.removeEventListener('change', handleChange);
      heightMql.removeEventListener('change', handleChange);
    };
  }, []);

  // Tablet Screen detected
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)');
    const handleChange = (e) => {
      setIsTablet(e.matches);
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Language dropdown: close on outside click or Escape
  useEffect(() => {
    if (!isLangMenuOpen) return;

    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isLangMenuOpen]);

  // viewOnlyMode is a pure render gate here -- it hides the editor toggle and the Save/Undo/Redo
  // clusters above, but deliberately doesn't write to the persisted editor-mode preference or
  // push a `false` back to the page. Each page instead derives its *effective* editor mode as
  // `editorModeEnabled && !viewOnly`, so an admin who wanders into a read-only level keeps the
  // toggle state they had and gets it back untouched on the next level they can edit.

  const handleReload = () => {
    window.location.href = userIsAuthenticated ? '/dashboard' : '/';
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <div className={`Navbar ${editorMode ? 'editor-controls-open' : ''}`}>
        <div className="nav-left">
          <div className="logo-text" onClick={() => handleReload()}>
            MindfulCare
          </div>
          {userIsAuthenticated && userIsStaff && !viewOnlyMode && (
            editorMode ? (
            <div className={`nav-history-controls ${editorMode && !isMobile ? 'editor-mode-active' : 'editor-mode-hidden'}`}>
              <div className="nav-divider"></div>
              <button className="history-btn save-btn" onClick={onSave} title={t('navbar.save')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                {t('navbar.save')}
              </button>
              <button className="history-btn" onClick={onHistory} title={t('navbar.history')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              </button>
              <div className="nav-divider"></div>
              <button className="history-btn" onClick={onUndo} disabled={!canUndo} title={t('navbar.undo')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path></svg>
              </button>
              <button className="history-btn" onClick={onRedo} disabled={!canRedo} title={t('navbar.redo')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"></path></svg>
              </button>
              <div className="nav-divider"></div>
              <button className="history-btn" onClick={onDecreaseLessonSectionSize} title="Decrease lesson section size">-</button>
              <span className="section-scale-label">{Math.round(lessonSectionScale * 100)}%</span>
              <button className="history-btn" onClick={onIncreaseLessonSectionSize} title="Increase lesson section size">+</button>
            </div>
            ) : ( historyLogView ? (
            <div className={`nav-history-controls ${historyLogView && !isMobile ? 'editor-mode-active' : 'editor-mode-hidden'}`}>
              <div className="nav-divider"></div>
              <button className="history-btn" onClick={onHistory} title={t('navbar.history')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              </button>
            </div>) : (
              editorModeLite && (
              <div className={`nav-history-controls ${editorModeLite && !isMobile ? 'editor-mode-active' : 'editor-mode-hidden'}`}>
                <div className="nav-divider"></div>
                <button className="history-btn save-btn" onClick={onSave} title={t('navbar.save')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                </button>
                <button className="history-btn" onClick={onHistory} title={t('navbar.history')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                </button>
                <div className="nav-divider"></div>
                <button className="history-btn" onClick={onUndo} disabled={!canUndo} title={t('navbar.undo')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"></path></svg>
                </button>
                <button className="history-btn" onClick={onRedo} disabled={!canRedo} title={t('navbar.redo')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"></path></svg>
                </button>
              </div>
              )
            ))
          )}
        </div>

        {isMobile && (
          <>
            <button className="chat-btn" title="Chat" style={{ width: '42px', height: '42px', marginLeft: 'auto'}} onClick={toggleChatWidget}>
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
              >
                  <path d="M20 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-4.1-1.05L3 20l1.05-4.4A8.4 8.4 0 0 1 3 11.5a8.5 8.5 0 1 1 17 0Z" />
                  <path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01" />
              </svg><span className="chat-notif-badge" style={{ display: 'none' }}>!</span>
            </button>

            <button className="notification-btn" title="Notifications" style={{ width: '42px', height: '42px'}} onClick={toggleNotifications}>
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
                focusable="false"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M10 21h4" />
              </svg>
              {/*🔔*/}
              <span className="bell-notif-badge" id="notifBadge" style={{ display: 'none' }}>!</span>
            </button>
          </>
        )}
        {userIsStaff && isTablet && !isMobile && (
          <>
            {userIsAuthenticated && userIsStaff && !viewOnlyMode && !isMobile && isTablet && (
              <>
                <button className={`preview-btn ${localEditorMode && !isMobile && isTablet ? 'active' : ''}`} id="btnPreview" style={{width: 'auto', maxWidth: '120px', marginLeft: 'auto', marginRight: '3px'}} onClick={toggleEditorMode}>
                  {localEditorMode ? t('navbar.editorOn') : t('navbar.editorOff')}
                </button>
                <button className="notification-btn" title="Notifications" style={{ width: '42px', height: '42px'}} onClick={toggleNotifications}>
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                    focusable="false"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                    <path d="M10 21h4" />
                  </svg>
                  {/*🔔*/}
                  <span className="bell-notif-badge" id="notifBadge" style={{ display: 'none' }}>!</span>
                </button>
              </>
            )}
            {userIsAuthenticated && viewOnlyMode && !isMobile && isTablet && (
                <button className="notification-btn" title="Notifications" style={{ width: '42px', height: '42px', marginLeft: 'auto'}} onClick={toggleNotifications}>
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                    focusable="false"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                    <path d="M10 21h4" />
                  </svg>
                  {/*🔔*/}
                  <span className="bell-notif-badge" id="notifBadge" style={{ display: 'none' }}>!</span>
                </button>
            )}
          </>
        )}

        <button
          className={`nav-menu-toggle ${isMenuOpen ? 'active' : ''}`}
          type="button"
          aria-label={isMenuOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
          aria-expanded={isMenuOpen}
          aria-controls="primary-navigation"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div id="primary-navigation" className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
          {userIsAuthenticated && userIsStaff && !viewOnlyMode && !isMobile && !isTablet && (
            <button className={`preview-btn ${localEditorMode && !isMobile && !isTablet ? 'active' : ''}`} id="btnPreview" onClick={toggleEditorMode}>
              {localEditorMode ? t('navbar.editorOn') : t('navbar.editorOff')}
            </button>
          )}
          <a
            className="nav-link"
            href="/about_us"
            onClick={(e) => { e.preventDefault(); closeMenu(); navigate('/about_us'); }}
          >
            {t('navbar.aboutUs')}
          </a>
          {userIsAuthenticated ? (
            <>
              <a
                className="nav-link"
                href={`/profile/${getCurrentUser().username}`}
                onClick={(e) => { e.preventDefault(); closeMenu(); navigate(`/profile/${getCurrentUser().username}`, { state: { from: location.pathname } }); }}
                style={{ color: 'var(--primary)', fontWeight: 700 }}
              >
                {t('navbar.profile')}
              </a>
              {!isMobile && !isTablet && (
                <button className="notification-btn" title="Notifications" onClick={toggleNotifications}>
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                    focusable="false"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                    <path d="M10 21h4" />
                  </svg>
                  {/*🔔*/}
                  <span className="bell-notif-badge" id="notifBadge" style={{ display: 'none' }}>!</span>
                </button>
              )}
              <button className="btn-logout" onClick={async () => {
                try {
                  await logoutUser();
                } catch (error) {
                  console.error('Logout error:', error);
                } finally {
                  window.location.replace('/');
                }
              }}>{t('navbar.logout')}</button>
            </>
          ) : (
            <button className="btn-login" onClick={() => handleLoggingIn()}>{t('navbar.login')}</button>
          )}
          <div className="lang-dropdown" ref={langDropdownRef}>
            <button
              type="button"
              className="btn-language"
              aria-haspopup="listbox"
              aria-expanded={isLangMenuOpen}
              onClick={() => setIsLangMenuOpen((open) => !open)}
            >
              {t(`navbar.languages.${currentLanguage}`)}
              <svg className="lang-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {isLangMenuOpen && (
              <ul className="lang-menu" role="listbox" aria-label={t('navbar.language')}>
                {LANGUAGE_OPTIONS.map((language) => (
                  <li key={language} role="option" aria-selected={language === currentLanguage}>
                    <button
                      type="button"
                      className={`lang-menu-item ${language === currentLanguage ? 'active' : ''}`}
                      onClick={() => changeLanguage(language)}
                    >
                      {t(`navbar.languages.${language}`)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      {userIsAuthenticated && (
        <Notification />
      )}
      {userIsAuthenticated && userIsStaff && (
        <Chat isOpen={isChatOpen} onToggle={toggleChatWidget} />
      )}
    </>

  );
}

export default Navbar;
